import { useCallback, useState } from 'react'
import {
  getAvailableDates,
  getAvailableTimeSlots,
  TimeSlotValue,
} from '../services/cinch-servicing'
import { AppointmentType } from '../types'
import { datadogRum } from '@datadog/browser-rum'

export type AppointmentAvailabilityErrorType =
  | 'no_dates_available'
  | 'no_time_slots_available'
  | 'api_error'
  | null

export const useFetchAppointmentAvailability = () => {
  const [loading, setLoading] = useState(false)
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false)
  const [appointmentDates, setAppointmentDates] = useState<string[]>()
  const [appointmentTypeId, setAppointmentTypeId] = useState<number>(5)
  const [timeSlots, setTimeSlots] = useState<TimeSlotValue[]>()
  const [error, setError] = useState<Error | null>(null)
  const [errorType, setErrorType] =
    useState<AppointmentAvailabilityErrorType>(null)

  const fetchAppointmentDates = useCallback(
    async (
      dealerId: string | undefined,
      serviceOptionsSelectedNames: string | undefined,
      appointmentType: AppointmentType,
      dealerDisplayName?: string,
      allowedTime?: number,
    ) => {
      if (!dealerId || !serviceOptionsSelectedNames || !appointmentType) {
        datadogRum.addError(
          new Error('Cinch Servicing: Select DateTime Error'),
          {
            cinchServicing: true,
            dealerId,
            dealerDisplayName,
            serviceOptionsSelectedNames,
            appointmentType,
          },
        )
        setLoading(false)
        setError(new Error('Missing value'))
        setErrorType('api_error')
        return
      }

      setLoading(true)
      setError(null)
      setErrorType(null)

      try {
        const dates = await getAvailableDates({
          dealerId,
          serviceOptions: serviceOptionsSelectedNames,
          appointmentType,
          allowedTime,
        })
        if (dates && dates.length > 0 && dates[0].datesAvailable?.length > 0) {
          setAppointmentDates(dates[0].datesAvailable)
          setAppointmentTypeId(dates[0].id)
          setError(null)
          setErrorType(null)
        } else {
          datadogRum.addError(
            new Error('Cinch Servicing: Get Available Dates None Available'),
            {
              cinchServicing: true,
              dealerId,
              dealerDisplayName,
              serviceOptionsSelectedNames,
              appointmentType,
            },
          )
          setError(new Error('Failed to get appointment dates'))
          setErrorType('no_dates_available')
          setAppointmentDates(undefined)
        }
      } catch (error) {
        setError(error as Error)
        setErrorType('api_error')
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const fetchAppointmentTimes = useCallback(
    async (
      dealerId: string | undefined,
      appointmentTypeId: number,
      date: string,
      dealerDisplayName?: string,
    ) => {
      if (!dealerId || !appointmentTypeId || !date) {
        datadogRum.addError(
          new Error('Cinch Servicing: Select TimeSlot Error'),
          {
            cinchServicing: true,
            dealerId,
            dealerDisplayName,
            appointmentTypeId,
            date,
          },
        )
        setTimeSlotsLoading(false)
        setError(new Error('Missing value'))
        setErrorType('api_error')
        return
      }

      setTimeSlotsLoading(true)
      setError(null)
      setErrorType(null)

      try {
        const availableTimes = await getAvailableTimeSlots({
          dealerId,
          appointmentTypeId,
          date,
        })
        if (
          availableTimes &&
          availableTimes.timeSlots &&
          availableTimes.timeSlots.length > 0
        ) {
          setTimeSlots(availableTimes.timeSlots)
          setError(null)
          setErrorType(null)
        } else {
          datadogRum.addError(
            new Error('Cinch Servicing: TimeSlots None Available'),
            {
              cinchServicing: true,
              dealerId,
              dealerDisplayName,
              appointmentTypeId,
              date,
            },
          )
          setError(new Error('Failed to get time slots'))
          setErrorType('no_time_slots_available')
          setTimeSlots(undefined)
        }
      } catch (error) {
        setError(error as Error)
        setErrorType('api_error')
      } finally {
        setTimeSlotsLoading(false)
      }
    },
    [],
  )

  return {
    fetchAppointmentDates,
    fetchAppointmentTimes,
    appointmentDates,
    appointmentTypeId,
    timeSlots,
    timeSlotsLoading,
    loading,
    error,
    errorType,
  }
}
